import { message } from 'antd';
import { action, computed, observable, reaction } from 'mobx';
import { uniqBy } from 'lodash';
import { ProjectsRootVisualStore, RouterStore, ProjectsStore } from '../../common/stores';
import { TestProjectsNavigation } from '../routes';
import { TestProjectService } from '../services';
import { TestProjectRunResults, TestRunResultPreviewModel, TestRunTopicMetaField } from '../types';
import TestProjectsVisualStore from './TestProjectsVisualStore';
import TestProjectDataStore from './TestProjectDataStore';
import { AdministrationService } from '../../project_management/services/AdministrationService';
import { PackageState } from '../../common/models';
import { TestProjectChange } from '../../common/types';

type TestProjectRouteParams = {
    projectId?: string;
    testProjectId?: string;
    runId?: string;
};

export default class TestProjectDashboardStore {
    @observable.ref
    testProjectDataStore: TestProjectDataStore | undefined;

    @computed
    get currentTestProjectDataStore() {
        const dataStore = this.testProjectDataStore;
        return dataStore?.projectId === this.currentProject?.id ? dataStore : undefined;
    }

    @observable
    testRunInProgress = false;

    @observable
    isLoadingTestRuns = false;

    @observable
    testRuns: TestProjectRunResults[] = [];

    @observable
    currentTestRunId: string | undefined;

    @observable
    currentTestRunResults: TestProjectRunResults | undefined;

    @observable
    testResultIsLoading = false;

    @observable
    previewWindow: Window | undefined | null;

    private testRunsRequest = 0;
    private runResultsRequest = 0;

    @computed
    get packages() {
        return this.testProjectsStore.packages;
    }

    @computed
    get busyPackages() {
        const busyProjectPackages = this.packages.filter(p => p.serverState === PackageState.Busy).map(p => p.id);
        return [...this.projectsBaseStore.busyPackages, ...busyProjectPackages];
    }

    @computed
    get currentProject() {
        return this.projectsStore.currentProject;
    }

    @computed
    get projectIsLocked() {
        const dataStore = this.testProjectDataStore;

        return this.testRunInProgress || !dataStore?.canUseProject || dataStore.testProject?.isRunning === true;
    }

    @computed
    get documentsProcessedCount() {
        if (!this.currentTestRunResults) {
            return 0;
        }

        return uniqBy(this.currentTestRunResults.topics, t => t.packageId ?? t.packageName).length;
    }

    @computed
    get documentsWithBaselineCount() {
        if (!this.currentTestRunResults) {
            return 0;
        }

        const uniquePackages = uniqBy(this.currentTestRunResults.topics, t => t.packageId ?? t.packageName);
        return uniquePackages.filter(pkg => {
            const key = pkg.packageId ?? pkg.packageName;
            const packageTopics = this.currentTestRunResults!.topics.filter(
                t => (t.packageId ?? t.packageName) === key
            );
            return packageTopics.every(t => t.baseline != null && t.baseline.trim() !== '');
        }).length;
    }

    @computed
    get baselineCoveragePercentage() {
        if (!this.currentTestRunResults) {
            return 0;
        }

        const totalDocuments = this.currentTestRunResults.documentsCount ?? this.documentsProcessedCount;
        if (totalDocuments === 0) {
            return 0;
        }

        return Math.round((this.documentsWithBaselineCount / totalDocuments) * 100);
    }

    @computed
    get packageIdsWithEmptyBaseline(): Set<string> {
        if (!this.currentTestRunResults) {
            return new Set();
        }

        const uniquePackages = uniqBy(this.currentTestRunResults.topics, t => t.packageId ?? t.packageName);
        const emptyKeys = new Set<string>();

        for (const pkg of uniquePackages) {
            const key = pkg.packageId ?? pkg.packageName;
            const packageTopics = this.currentTestRunResults.topics.filter(t => (t.packageId ?? t.packageName) === key);
            const hasEmptyBaseline = packageTopics.some(t => t.baseline == null || t.baseline.trim() === '');
            if (hasEmptyBaseline) {
                emptyKeys.add(key);
            }
        }

        return emptyKeys;
    }

    constructor(
        private projectsStore: ProjectsRootVisualStore,
        private routerStore: RouterStore,
        private testProjectsStore: TestProjectsVisualStore,
        private service: TestProjectService,
        private adminService: AdministrationService,
        public readonly projectsBaseStore: ProjectsStore
    ) {
        reaction(
            () => this.currentProject?.id,
            projectId => {
                if (this.testProjectDataStore && this.testProjectDataStore.projectId !== projectId) {
                    this.selectTestProject(undefined);
                }
            }
        );
    }

    @action.bound
    matchCurrentTestProjectData(params: TestProjectRouteParams) {
        const dataStore = this.testProjectDataStore;

        if (!params.projectId || !params.testProjectId) {
            this.selectTestProject(undefined);
            return;
        }

        if (dataStore?.projectId !== params.projectId || dataStore.testProjectId !== params.testProjectId) {
            this.selectTestProject(
                new TestProjectDataStore(params.projectId, params.testProjectId, this.service, this.testProjectsStore)
            );
        }

        if (this.currentTestRunId !== params.runId) {
            this.currentTestRunId = params.runId;
            this.currentTestRunResults = undefined;
            this.testResultIsLoading = false;
        }
    }

    @action.bound
    handleTestProjectChanges(change: TestProjectChange) {
        const dataStore = this.testProjectDataStore;

        if (change.projectId !== dataStore?.projectId || change.id !== dataStore.testProjectId) {
            return;
        }

        dataStore.handleTestProjectChanges(change);

        if (change.isRunning === false) {
            this.loadTestRuns();
        }
    }

    @action.bound
    goBackToList() {
        if (this.currentProject) {
            this.routerStore.push(
                TestProjectsNavigation.TestProjectsPage.replace(':projectId', this.currentProject.id)
            );
        }
    }

    @action.bound
    goToBaselines() {
        const dataStore = this.testProjectDataStore;

        if (dataStore) {
            this.routerStore.push(
                TestProjectsNavigation.TestProjectBaselinesPage.replace(':projectId', dataStore.projectId).replace(
                    ':testProjectId',
                    dataStore.testProjectId
                )
            );
        }
    }

    @action.bound
    goToDashboard() {
        const dataStore = this.testProjectDataStore;

        if (dataStore) {
            this.routerStore.push(
                TestProjectsNavigation.TestProjectDashboardPage.replace(':projectId', dataStore.projectId).replace(
                    ':testProjectId',
                    dataStore.testProjectId
                )
            );
        }
    }

    @action.bound
    goToTestResults(runId: string) {
        const dataStore = this.testProjectDataStore;

        if (dataStore) {
            this.routerStore.push(
                TestProjectsNavigation.TestProjectRunResultsPage.replace(':projectId', dataStore.projectId)
                    .replace(':testProjectId', dataStore.testProjectId)
                    .replace(':runId', runId)
            );
        }
    }

    @action.bound
    async runTestProject() {
        const dataStore = this.testProjectDataStore;

        if (!dataStore?.canUseProject || this.projectIsLocked) {
            return;
        }

        this.testRunInProgress = true;

        try {
            const result = await this.service.runTestProject(dataStore.projectId, dataStore.testProjectId);

            if (dataStore !== this.testProjectDataStore) {
                return;
            }

            if (result.isOk()) {
                dataStore.markRunStarted();
                message.success('Test project run started successfully');
            } else {
                message.error('Failed to run test project');
            }
        } catch (error) {
            console.error(error);

            if (dataStore === this.testProjectDataStore) {
                message.error('Failed to run test project');
            }
        } finally {
            if (dataStore === this.testProjectDataStore) {
                this.testRunInProgress = false;
            }
        }
    }

    @action.bound
    async abortTestProjectRun() {
        const dataStore = this.testProjectDataStore;

        if (!dataStore?.canUseProject) {
            return;
        }

        try {
            const result = await this.service.abortTestProjectRun(dataStore.projectId, dataStore.testProjectId);

            if (dataStore !== this.testProjectDataStore) {
                return;
            }

            if (result.isOk()) {
                dataStore.markRunAborting();
                const projects = this.testProjectsStore.testProjects.slice();
                const project = projects.find(item => item.id === dataStore.testProjectId);

                if (project) {
                    project.isAborting = true;
                }

                this.testProjectsStore.testProjects = projects;
                message.success('Test project run abort initiated successfully');
            } else {
                message.error('Failed to abort test project run');
            }
        } catch (error) {
            console.error(error);

            if (dataStore === this.testProjectDataStore) {
                message.error('Failed to abort test project run');
            }
        } finally {
            if (dataStore === this.testProjectDataStore) {
                this.testRunInProgress = false;
            }
        }
    }

    @action.bound
    async cleanTestProject() {
        const dataStore = this.testProjectDataStore;

        if (!dataStore) {
            return;
        }

        try {
            const result = await this.service.cleanTestProject(dataStore.projectId, dataStore.testProjectId);

            if (dataStore === this.testProjectDataStore) {
                if (result.isOk()) {
                    message.success('Test project has been cleaned');
                } else {
                    message.error('Failed to clean test project');
                }
            }
        } catch (error) {
            console.error(error);

            if (dataStore === this.testProjectDataStore) {
                message.error('Failed to clean test project');
            }
        } finally {
            await dataStore.loadTestProject();
        }
    }

    @action.bound
    setTestRuns(testRuns: TestProjectRunResults[]) {
        this.testRuns = testRuns;
    }

    @action.bound
    async loadTestRuns() {
        const dataStore = this.testProjectDataStore;

        if (!dataStore) {
            return;
        }

        const request = ++this.testRunsRequest;
        const isCurrent = () => dataStore === this.testProjectDataStore && request === this.testRunsRequest;
        this.isLoadingTestRuns = true;

        try {
            const results = await this.service.getTestProjectRunResults(dataStore.projectId, dataStore.testProjectId);

            if (isCurrent()) {
                this.testRuns = results;
            }
        } catch (error) {
            console.error(error);

            if (isCurrent()) {
                message.error('Failed to load test runs');
            }
        } finally {
            if (isCurrent()) {
                this.isLoadingTestRuns = false;
            }
        }
    }

    @action.bound
    async exportTestProjectRunToExcel(testRunId: string) {
        const dataStore = this.testProjectDataStore;

        if (dataStore) {
            await this.service.exportTestProjectRunResuts(dataStore.projectId, dataStore.testProjectId, testRunId);
        }
    }

    @action.bound
    setCurrentTestRunResults(testRunResults: TestProjectRunResults | undefined) {
        this.currentTestRunResults = testRunResults;
    }

    @action.bound
    async loadTestRunResults() {
        const dataStore = this.testProjectDataStore;
        const runId = this.currentTestRunId;

        if (!dataStore || !runId) {
            return;
        }

        const request = ++this.runResultsRequest;
        const isCurrent = () =>
            dataStore === this.testProjectDataStore &&
            runId === this.currentTestRunId &&
            request === this.runResultsRequest;
        this.testResultIsLoading = true;

        try {
            const results = await this.service.getTestRunResultById(
                dataStore.projectId,
                dataStore.testProjectId,
                runId
            );

            if (isCurrent()) {
                this.currentTestRunResults = results;
            }
        } catch (error) {
            console.error(error);

            if (isCurrent()) {
                message.error('Failed to load test run results');
            }
        } finally {
            if (isCurrent()) {
                this.testResultIsLoading = false;
            }
        }
    }

    @action.bound
    async updateBaselineFromTestRun(baselineId: string, value: string) {
        const dataStore = this.testProjectDataStore;
        const runId = this.currentTestRunId;

        if (!dataStore || !runId) {
            return;
        }

        try {
            const result = await this.service.updateBaselineFromTestRun(
                dataStore.projectId,
                dataStore.testProjectId,
                runId,
                baselineId,
                value
            );

            if (dataStore !== this.testProjectDataStore || runId !== this.currentTestRunId) {
                return;
            }

            if (result.isOk()) {
                const topic = this.currentTestRunResults?.topics.find(item => item.baselineId === baselineId);

                if (topic) {
                    topic.modifiedBaseline = value;
                }

                message.success('Baseline updated successfully');
            } else {
                message.error('Failed to update baseline from test run');
            }
        } catch (error) {
            console.error(error);

            if (dataStore === this.testProjectDataStore && runId === this.currentTestRunId) {
                message.error('Failed to update baseline from test run');
            }
        }
    }

    @action.bound
    async updateBaselineFuzzyFromTestRun(baselineId: string, fuzzy: number) {
        const dataStore = this.testProjectDataStore;
        const runId = this.currentTestRunId;

        if (!dataStore || !runId) {
            return;
        }

        try {
            const result = await this.service.updateBaselineFuzzyFromTestRun(
                dataStore.projectId,
                dataStore.testProjectId,
                runId,
                baselineId,
                fuzzy
            );

            if (dataStore !== this.testProjectDataStore || runId !== this.currentTestRunId) {
                return;
            }

            if (result.isOk()) {
                const topic = this.currentTestRunResults?.topics.find(item => item.baselineId === baselineId);

                if (topic) {
                    topic.modifiedBaselineFuzzy = fuzzy;
                }

                message.success('Baseline updated successfully');
            } else {
                message.error('Failed to update baseline from test run');
            }
        } catch (error) {
            console.error(error);

            if (dataStore === this.testProjectDataStore && runId === this.currentTestRunId) {
                message.error('Failed to update baseline from test run');
            }
        }
    }

    @action.bound
    openPreviewTopicResultPreviewWindow(
        packageId: string | null,
        topicId: string,
        blocks: TestRunTopicMetaField[] = []
    ) {
        if (!this.testProjectDataStore || !packageId) {
            return;
        }

        const url = `${window.location.origin}/projects/${this.testProjectDataStore.projectId}/test-project/${this.testProjectDataStore.testProjectId}/preview`;
        const previewData: TestRunResultPreviewModel = {
            packageId,
            topicId,
            blocks
        };

        localStorage.setItem('test-project-preview', JSON.stringify(previewData));

        if (!this.previewWindow || this.previewWindow.closed) {
            this.previewWindow = window.open(
                url,
                'winPop',
                'location=yes,height=700,width=1100,scrollbars=yes,status=yes'
            );
        } else {
            this.previewWindow.dispatchEvent(new Event('storage'));
            this.previewWindow.focus();
        }
    }

    @action.bound
    async reparsePackage(packageId: string) {
        try {
            const resp = await this.adminService.parseByPackage(packageId);
            if (!resp.isOk()) {
                message.error('Failed to reparse package');
            } else {
                message.success('Package reparse started successfully');
            }
        } catch (err) {
            console.error(err);
        }
    }

    @action.bound
    async reparsePackagesBatch(packageIds: string[]) {
        if (packageIds.length === 0) {
            return;
        }

        try {
            const resp = await this.adminService.parsePackagesBatch(packageIds);
            if (!resp.isOk()) {
                message.error('Failed to reparse packages');
            } else {
                message.success(`Batch reparse started successfully for ${packageIds.length} package(s)`);
            }
        } catch (err) {
            console.error(err);
            message.error('Failed to reparse packages');
        }
    }
    @action.bound
    private selectTestProject(dataStore: TestProjectDataStore | undefined) {
        this.testProjectDataStore = dataStore;
        this.testRuns = [];
        this.isLoadingTestRuns = false;
        this.testRunInProgress = false;
        this.currentTestRunId = undefined;
        this.currentTestRunResults = undefined;
        this.testResultIsLoading = false;
    }
}
